// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  confirmCardScanJob,
  createCardScanJob,
  discardCollectionItemsBulk,
  getCardMarket,
  getCardScanJob,
  saveCollectionItemsBulk,
} from "../api/cards";
import CardAutocompleteInput from "./CardAutocompleteInput";
import SharedSelect from "./SharedSelect";
import { useToast } from "./ToastProvider";
import {
  IMAGE_INPUT_ACCEPT_ATTR,
  MAX_IMAGE_BYTES_DEFAULT,
  validateImageFile,
} from "../utils/imageUploadValidation";
import { canonicalGameToDisplayLabel } from "../utils/canonicalGames";
import { downloadSessionPdf } from "../utils/sessionPdf";
import "../styles/CardScanPanel.css";

const DEFAULT_GAME_OPTIONS = [
  "Magic: The Gathering",
  "Pokémon",
  "Yu-Gi-Oh!",
  "One Piece",
];

function filePreviewUrl(file) {
  return file ? URL.createObjectURL(file) : "";
}

function revokePreviewUrl(url) {
  if (url) {
    URL.revokeObjectURL(url);
  }
}

function normalizeRecognitionGame(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "pokemon" || normalized === "pokémon") return "pokemon";
  if (normalized === "magic: the gathering" || normalized === "mtg" || normalized === "magic") return "mtg";
  if (normalized === "yu-gi-oh!" || normalized === "yu-gi-oh" || normalized === "yugioh") return "yugioh";
  return null;
}

function getProgressStage(job) {
  const status = String(job?.status || "");
  const progress = Number(job?.progress || 0);
  if (status === "completed") return "Scan complete";
  if (status === "failed") return "Scan failed";
  if (progress >= 65) return "Matching against card catalog";
  if (progress >= 15) return "Analyzing card images";
  return "Preparing scan";
}

function getProgressPercent(job) {
  const status = String(job?.status || "");
  const raw = Number(job?.progress || 0);
  if (status === "completed" || status === "failed") return 100;
  if (!Number.isFinite(raw) || raw <= 0) return 5;
  if (raw < 15) return Math.max(8, raw);
  if (raw < 65) return Math.max(24, raw);
  return Math.max(78, raw);
}

function normalizeAuthenticityDecision(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "pass" || normalized === "review" || normalized === "fail" || normalized === "unverified") {
    return normalized;
  }
  return "unverified";
}

function getAuthenticityDecisionLabel(decision) {
  if (decision === "pass") return "Passed";
  if (decision === "review") return "Needs review";
  if (decision === "fail") return "Failed authenticity";
  return "Unverified";
}

function getRecommendedActionLabel(action) {
  if (action === "confirm_candidate") return "You can confirm this card from the scanner.";
  if (action === "retry_images") return "Retry the photos for a stronger verification.";
  if (action === "verify_slab") return "Verify the slab details before using this scan.";
  if (action === "admin_review") return "Send this card for deeper review before using it.";
  return "Select the correct card if you still want to continue.";
}

function sanitizeScannerWarnings(list) {
  return (Array.isArray(list) ? list : [])
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .filter(
      (item) =>
        item !== "No reliable catalog candidate was available for strict authenticity comparison." &&
        item !== "No reliable Pokémon, MTG, or Yu-Gi-Oh! match was found from the scan. Please confirm the card manually."
    );
}

function clampNumber(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function createSessionItemId() {
  return `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function getCardMarketKey(card) {
  if (!card?.game || !card?.cardId) return "";
  return `${String(card.game).trim().toLowerCase()}:${String(card.cardId).trim()}`;
}

function hasEmbeddedMarket(card) {
  return Boolean(card && Object.prototype.hasOwnProperty.call(card, "market"));
}

function formatMarketPrice(value, currency = "USD") {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch {
    return `$${numeric.toFixed(2)}`;
  }
}

function formatMarketRange(market) {
  const low = formatMarketPrice(market?.low, market?.currency);
  const high = formatMarketPrice(market?.high, market?.currency);
  if (low && high) return `${low} - ${high}`;
  return low || high || "";
}

function formatMarketSource(market) {
  return [market?.source, market?.provider, market?.variant].filter(Boolean).join(" • ");
}

function formatMarketFetchedAt(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function buildCollectionDuplicateKey({ cardGame, cardId, condition, gradingCompany, gradeNumber }) {
  if (!cardGame || !cardId) return "";
  return [
    String(cardGame).trim().toLowerCase(),
    String(cardId).trim().toLowerCase(),
    String(condition || "").trim().toLowerCase(),
    String(gradingCompany || "").trim().toLowerCase(),
    String(gradeNumber || "").trim().toLowerCase(),
  ].join("|");
}

function getSessionStateLabel(item) {
  if (item.captureStatus === "saved") return "Saved";
  if (item.captureStatus === "ready_for_review") return "Ready for review";
  if (item.captureStatus === "scanning") return "Scanning";
  if (item.captureStatus === "failed") return "Failed";
  if (item.captureStatus === "confirmed") return "Confirmed";
  return "Captured";
}

function getSessionStateCopy(item) {
  if (item.captureStatus === "saved") {
    return item.saveOutcome === "merged_duplicate"
      ? "This card was merged into an existing saved collection entry."
      : "This card has been saved into your collection.";
  }
  if (item.captureStatus === "ready_for_review") return "Scan is ready to review and confirm.";
  if (item.captureStatus === "scanning") return "We’re analyzing this pair now.";
  if (item.captureStatus === "failed") return "Scan failed. You can retry or retake the images.";
  if (item.captureStatus === "confirmed") return "This card is staged in Ready to Save.";
  return "Front and back images captured.";
}

function getGuideCropRegion({ stageRect, frameRect, sourceWidth, sourceHeight }) {
  if (!stageRect || !frameRect || !sourceWidth || !sourceHeight) return null;
  if (!stageRect.width || !stageRect.height || !frameRect.width || !frameRect.height) return null;

  const sourceAspect = sourceWidth / sourceHeight;
  const stageAspect = stageRect.width / stageRect.height;

  let renderedWidth = stageRect.width;
  let renderedHeight = stageRect.height;

  if (sourceAspect > stageAspect) {
    renderedHeight = stageRect.height;
    renderedWidth = renderedHeight * sourceAspect;
  } else {
    renderedWidth = stageRect.width;
    renderedHeight = renderedWidth / sourceAspect;
  }

  const offsetX = (renderedWidth - stageRect.width) / 2;
  const offsetY = (renderedHeight - stageRect.height) / 2;
  const scaleX = sourceWidth / renderedWidth;
  const scaleY = sourceHeight / renderedHeight;

  const relativeLeft = frameRect.left - stageRect.left;
  const relativeTop = frameRect.top - stageRect.top;

  const sx = clampNumber((relativeLeft + offsetX) * scaleX, 0, sourceWidth);
  const sy = clampNumber((relativeTop + offsetY) * scaleY, 0, sourceHeight);
  const maxCropWidth = sourceWidth - sx;
  const maxCropHeight = sourceHeight - sy;
  const sw = clampNumber(frameRect.width * scaleX, 1, maxCropWidth);
  const sh = clampNumber(frameRect.height * scaleY, 1, maxCropHeight);

  return {
    sx: Math.round(sx),
    sy: Math.round(sy),
    sw: Math.max(1, Math.round(sw)),
    sh: Math.max(1, Math.round(sh)),
  };
}

export default function CardScanPanel({
  flowContext,
  gameHint = "",
  title = "AI Card Scan",
  subtitle = "Upload front and back images to recognize the card and review advisory analysis.",
  launchMode = "upload",
  variant = "default",
  externalFrontPreview = "",
  externalBackPreview = "",
  onConfirmed,
  onPostTrade,
  onRequestClose,
  onCameraVisibilityChange,
  onFilesChange,
  readyToSaveItems = [],
  savedCollectionItems = [],
  onCollectionChanged,
}) {
  const { showToast } = useToast();
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState("");
  const [backPreview, setBackPreview] = useState("");
  const [job, setJob] = useState(null);
  const [polling, setPolling] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showEditDetails, setShowEditDetails] = useState(false);
  const [showResultDetails, setShowResultDetails] = useState(false);
  const [manualOverrideMode, setManualOverrideMode] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [manualGame, setManualGame] = useState(gameHint || DEFAULT_GAME_OPTIONS[0]);
  const [manualCard, setManualCard] = useState(null);
  const [manualCardName, setManualCardName] = useState("");
  const [condition, setCondition] = useState("");
  const [gradingCompany, setGradingCompany] = useState("raw");
  const [gradeNumber, setGradeNumber] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [manualMarketCache, setManualMarketCache] = useState({});
  const [marketLoadingByKey, setMarketLoadingByKey] = useState({});
  const [scanSession, setScanSession] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [cameraStep, setCameraStep] = useState("front");
  const [cameraTorchSupported, setCameraTorchSupported] = useState(false);
  const [cameraTorchEnabled, setCameraTorchEnabled] = useState(false);
  const [cameraReplaceTargetId, setCameraReplaceTargetId] = useState("");
  const [batchModeEnabled, setBatchModeEnabled] = useState(false);
  const [stagedFrontCapture, setStagedFrontCapture] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkDiscarding, setBulkDiscarding] = useState(false);
  const videoRef = useRef(null);
  const cameraStageRef = useRef(null);
  const cameraFrameRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const frontPreviewRef = useRef("");
  const backPreviewRef = useRef("");
  const stagedFrontCaptureRef = useRef(null);
  const scanSessionRef = useRef([]);
  const onFilesChangeRef = useRef(onFilesChange);
  const launchHandledRef = useRef(false);
  const sessionOrderRef = useRef(0);

  const isCollectionFlow = flowContext === "collection";
  const requiresBackImage = !isCollectionFlow;
  const isNavbarModalVariant = variant === "navbarModal";
  const initialCameraLaunchPendingRef = useRef(isNavbarModalVariant && launchMode === "camera");
  const cameraSupported = typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia);
  const scanCompleted = job?.status === "completed";
  const candidates = Array.isArray(job?.result?.candidates) ? job.result.candidates : [];
  const selectedCard = manualCard || selectedCandidate;
  const detectedGame = job?.result?.analysis?.detectedClues?.game || "";
  const recognitionConfidence = job?.result?.analysis?.confidence;
  const activeGame = manualCard?.game || selectedCandidate?.game || detectedGame || gameHint || manualGame;
  const hintedRecognitionGame = normalizeRecognitionGame(gameHint);
  const scanInProgress = job?.status === "pending" || job?.status === "processing";
  const progressStage = getProgressStage(job);
  const progressPercent = getProgressPercent(job);
  const primaryResultCard = selectedCard || selectedCandidate || candidates[0] || null;
  const scanAnalysis = job?.result?.analysis || {};
  const authenticityDecision = normalizeAuthenticityDecision(scanAnalysis?.authenticityDecision);
  const authenticityDecisionLabel = getAuthenticityDecisionLabel(authenticityDecision);
  const authenticityChecks = Array.isArray(scanAnalysis?.authenticityChecks) ? scanAnalysis.authenticityChecks : [];
  const missingEvidence = Array.isArray(scanAnalysis?.missingEvidence) ? scanAnalysis.missingEvidence : [];
  const recommendedAction = scanAnalysis?.recommendedAction || "manual_override";
  const confirmationAllowed = Boolean(job?.result?.confirmationAllowed ?? scanAnalysis?.confirmationAllowed);
  const scannerBlocked = scanCompleted && !confirmationAllowed;
  const selectedCandidateStrong = Boolean(selectedCandidate?.cardId && !scannerBlocked);
  const showSummaryConfidence = Boolean(selectedCandidateStrong && recognitionConfidence != null);
  const manualOverrideNote = scannerBlocked
    ? "Review Card Choice lets you pick the correct catalog card when the scanner cannot verify this result."
    : "";
  const scanSupportWarning =
    gameHint && !hintedRecognitionGame
      ? "Scanner recognition v1 is tuned for Pokémon, MTG, and Yu-Gi-Oh!. You can still select the card yourself."
      : "";
  const resolveMarketForCard = useCallback((card) => {
    if (!card) return undefined;
    if (hasEmbeddedMarket(card)) {
      return card.market ?? null;
    }
    const key = getCardMarketKey(card);
    return key && Object.prototype.hasOwnProperty.call(manualMarketCache, key)
      ? manualMarketCache[key]
      : undefined;
  }, [manualMarketCache]);
  const primaryResultMarket = resolveMarketForCard(primaryResultCard);
  const selectedCardMarket = resolveMarketForCard(selectedCard);
  const selectedCardMarketKey = getCardMarketKey(selectedCard);
  const selectedCardMarketLoading = Boolean(
    selectedCard &&
      !hasEmbeddedMarket(selectedCard) &&
      selectedCardMarketKey &&
      marketLoadingByKey[selectedCardMarketKey] === "loading"
  );
  const primaryMarketHeadline = formatMarketPrice(
    primaryResultMarket?.market ?? primaryResultMarket?.mid ?? primaryResultMarket?.low ?? primaryResultMarket?.high,
    primaryResultMarket?.currency
  );
  const primaryMarketRange = formatMarketRange(primaryResultMarket);
  const primaryMarketSource = formatMarketSource(primaryResultMarket);
  const primaryMarketFetchedAt = formatMarketFetchedAt(primaryResultMarket?.fetchedAt);
  const selectedMarketHeadline = formatMarketPrice(
    selectedCardMarket?.market ?? selectedCardMarket?.mid ?? selectedCardMarket?.low ?? selectedCardMarket?.high,
    selectedCardMarket?.currency
  );
  const selectedMarketRange = formatMarketRange(selectedCardMarket);
  const selectedMarketSource = formatMarketSource(selectedCardMarket);
  const selectedMarketFetchedAt = formatMarketFetchedAt(selectedCardMarket?.fetchedAt);

  const activeSessionItem = useMemo(
    () => scanSession.find((item) => item.id === activeSessionId && !item.removedAt) || null,
    [scanSession, activeSessionId]
  );
  const activeConfirmedSessionItem = activeSessionItem?.confirmedCard ? activeSessionItem : null;
  const visibleSessionItems = useMemo(
    () => scanSession.filter((item) => !item.removedAt),
    [scanSession]
  );
  const batchScannableItems = useMemo(
    () =>
      visibleSessionItems.filter(
        (item) =>
          item.frontFile &&
          (requiresBackImage ? item.backFile : true) &&
          item.captureStatus !== "scanning" &&
          item.captureStatus !== "ready_for_review" &&
          item.captureStatus !== "confirmed" &&
          item.captureStatus !== "saved"
      ),
    [requiresBackImage, visibleSessionItems]
  );

  useEffect(() => {
    setManualGame(gameHint || DEFAULT_GAME_OPTIONS[0]);
  }, [gameHint]);

  useEffect(() => {
    frontPreviewRef.current = frontPreview;
  }, [frontPreview]);

  useEffect(() => {
    backPreviewRef.current = backPreview;
  }, [backPreview]);

  useEffect(() => {
    stagedFrontCaptureRef.current = stagedFrontCapture;
  }, [stagedFrontCapture]);

  useEffect(() => {
    scanSessionRef.current = scanSession;
  }, [scanSession]);

  useEffect(() => {
    onFilesChangeRef.current = onFilesChange;
  }, [onFilesChange]);

  useEffect(() => {
    return () => {
      revokePreviewUrl(frontPreviewRef.current);
      revokePreviewUrl(backPreviewRef.current);
      revokePreviewUrl(stagedFrontCaptureRef.current?.preview);
      scanSessionRef.current.forEach((item) => {
        revokePreviewUrl(item.frontPreview);
        revokePreviewUrl(item.backPreview);
      });
    };
  }, []);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    onFilesChangeRef.current?.({
      frontFile,
      backFile,
      frontPreview,
      backPreview,
    });
  }, [frontFile, backFile, frontPreview, backPreview]);

  useEffect(() => {
    if (!manualCard?.game || !manualCard?.cardId) return undefined;
    if (hasEmbeddedMarket(manualCard)) return undefined;

    const key = getCardMarketKey(manualCard);
    if (!key) return undefined;
    if (Object.prototype.hasOwnProperty.call(manualMarketCache, key)) return undefined;
    if (marketLoadingByKey[key] === "loading") return undefined;

    let cancelled = false;
    setMarketLoadingByKey((prev) => ({ ...prev, [key]: "loading" }));

    void (async () => {
      try {
        const response = await getCardMarket({
          game: manualCard.game,
          cardId: manualCard.cardId,
        });
        if (cancelled) return;
        setManualMarketCache((prev) =>
          Object.prototype.hasOwnProperty.call(prev, key)
            ? prev
            : { ...prev, [key]: response?.market ?? null }
        );
      } catch (_error) {
        if (cancelled) return;
        setManualMarketCache((prev) =>
          Object.prototype.hasOwnProperty.call(prev, key)
            ? prev
            : { ...prev, [key]: null }
        );
      } finally {
        if (!cancelled) {
          setMarketLoadingByKey((prev) => ({ ...prev, [key]: "done" }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [manualCard, manualMarketCache, marketLoadingByKey]);

  const makeSessionItem = useCallback((input = {}) => {
    sessionOrderRef.current += 1;
    const itemId = input.id || createSessionItemId();
    return {
      id: itemId,
      sessionItemId: input.sessionItemId || itemId,
      sessionOrder: input.sessionOrder || sessionOrderRef.current,
      frontFile: input.frontFile || null,
      backFile: input.backFile || null,
      frontPreview: input.frontPreview || "",
      backPreview: input.backPreview || "",
      captureStatus: input.captureStatus || "captured",
      jobId: input.jobId || "",
      jobStatus: input.jobStatus || "",
      job: input.job || null,
      error: input.error || "",
      collectionItemId: input.collectionItemId || "",
      confirmedCard: input.confirmedCard || null,
      condition: input.condition || "",
      gradingCompany: input.gradingCompany || "raw",
      gradeNumber: input.gradeNumber || "",
      quantity: input.quantity || "1",
      saveOutcome: input.saveOutcome || "",
      removedAt: input.removedAt || "",
      removedReason: input.removedReason || "",
    };
  }, []);

  const clearActiveWorkspace = useCallback(() => {
    setFrontFile(null);
    setBackFile(null);
    setFrontPreview("");
    setBackPreview("");
    setJob(null);
    setSelectedCandidate(null);
    setManualCard(null);
    setManualCardName("");
    setShowEditDetails(false);
    setShowResultDetails(false);
    setManualOverrideMode(false);
    setCondition("");
    setGradingCompany("raw");
    setGradeNumber("");
    setQuantity("1");
    setActiveSessionId("");
  }, []);

  const readyToSaveById = useMemo(
    () => new Map((Array.isArray(readyToSaveItems) ? readyToSaveItems : []).map((item) => [item.id, item])),
    [readyToSaveItems]
  );

  const sessionSummary = useMemo(() => {
    const savedKeys = new Set(
      (Array.isArray(savedCollectionItems) ? savedCollectionItems : [])
        .map((item) =>
          buildCollectionDuplicateKey({
            cardGame: item.cardGame,
            cardId: item.cardId,
            condition: item.condition,
            gradingCompany: item.gradingCompany,
            gradeNumber: item.gradeNumber,
          })
        )
        .filter(Boolean)
    );

    const visibleItems = scanSession
      .filter((item) => !item.removedAt)
      .slice()
      .sort((left, right) => Number(left.sessionOrder || 0) - Number(right.sessionOrder || 0));

    const seenSessionKeys = new Set();
    const detailsById = {};
    let added = 0;
    let duplicates = 0;
    let errors = 0;

    visibleItems.forEach((item) => {
      const confirmedCard = item.confirmedCard || {};
      const readyItem = item.collectionItemId ? readyToSaveById.get(item.collectionItemId) : null;
      const duplicateKey = buildCollectionDuplicateKey({
        cardGame: confirmedCard.game || readyItem?.cardGame,
        cardId: confirmedCard.cardId || readyItem?.cardId,
        condition: item.condition || readyItem?.condition,
        gradingCompany: item.gradingCompany || readyItem?.gradingCompany,
        gradeNumber: item.gradeNumber || readyItem?.gradeNumber,
      });

      let duplicateClassification = "";
      let duplicateSource = "";
      if (item.captureStatus !== "saved" && duplicateKey && savedKeys.has(duplicateKey)) {
        duplicateClassification = "saved_collection";
        duplicateSource = "saved_collection";
      } else if (item.captureStatus !== "saved" && duplicateKey && seenSessionKeys.has(duplicateKey)) {
        duplicateClassification = "session_duplicate";
        duplicateSource = "session_duplicate";
      } else if (duplicateKey && item.captureStatus !== "saved") {
        seenSessionKeys.add(duplicateKey);
      }

      const stagedInReadyToSave = Boolean(item.collectionItemId && readyToSaveById.has(item.collectionItemId));
      const isAdded = item.captureStatus === "confirmed" && stagedInReadyToSave;
      const isError =
        item.captureStatus === "failed" ||
        (item.jobStatus === "completed" &&
          item.captureStatus !== "confirmed" &&
          item.captureStatus !== "saved");

      if (isAdded) added += 1;
      if (duplicateClassification) duplicates += 1;
      if (isError) errors += 1;

      detailsById[item.id] = {
        duplicateKey,
        duplicateClassification,
        duplicateSource,
        removable: Boolean(duplicateClassification),
        isAdded,
        isError,
        stagedInReadyToSave,
      };
    });

    const exportRows = scanSession
      .slice()
      .sort((left, right) => Number(left.sessionOrder || 0) - Number(right.sessionOrder || 0))
      .map((item, index) => {
        const detail = detailsById[item.id] || {};
        const confirmedCard = item.confirmedCard || {};
        const status =
          item.removedReason === "duplicate"
            ? "Removed duplicate"
            : item.removedReason === "removed"
            ? "Removed from session"
            : getSessionStateLabel(item);
        return {
          "#": index + 1,
          Card: confirmedCard.cardName || "Pending card match",
          Game:
            canonicalGameToDisplayLabel(
              confirmedCard.game ||
                readyToSaveById.get(item.collectionItemId || "")?.cardGame ||
                ""
            ) || "-",
          Status: status,
          Added: detail.isAdded ? "Yes" : "No",
          Duplicate: detail.duplicateClassification ? detail.duplicateClassification.replace(/_/g, " ") : "No",
          Reason: item.error || item.removedReason || item.saveOutcome || "-",
        };
      });

    return {
      detailsById,
      totalScanned: scanSession.length,
      added,
      duplicates,
      errors,
      eligibleSaveIds: visibleItems
        .filter((item) => {
          const detail = detailsById[item.id] || {};
          return item.captureStatus === "confirmed" && detail.stagedInReadyToSave && !detail.duplicateClassification;
        })
        .map((item) => item.collectionItemId)
        .filter(Boolean),
      duplicateDiscardIds: visibleItems
        .filter((item) => {
          const detail = detailsById[item.id] || {};
          return detail.duplicateClassification && detail.stagedInReadyToSave && item.collectionItemId;
        })
        .map((item) => item.collectionItemId),
      removableDuplicateSessionIds: visibleItems
        .filter((item) => (detailsById[item.id] || {}).duplicateClassification)
        .map((item) => item.id),
      exportRows,
    };
  }, [readyToSaveById, savedCollectionItems, scanSession]);

  const applyCompletedJobState = useCallback((nextJob) => {
    if (!nextJob) return;
    if (nextJob?.status === "completed" && nextJob?.result?.selectedCandidate) {
      setSelectedCandidate(nextJob.result.selectedCandidate);
      setManualGame(nextJob.result.selectedCandidate.game || gameHint || DEFAULT_GAME_OPTIONS[0]);
      setShowEditDetails(false);
      setShowResultDetails(false);
      setManualOverrideMode(false);
    } else if (nextJob?.status === "completed") {
      setShowEditDetails(false);
      setShowResultDetails(false);
      setManualOverrideMode(false);
    }
  }, [gameHint]);

  useEffect(() => {
    if (!job?.id) return undefined;
    if (job.status !== "pending" && job.status !== "processing") return undefined;

    setPolling(true);
    const intervalId = window.setInterval(async () => {
      try {
        const nextJob = await getCardScanJob({ id: job.id });
        setJob(nextJob);
        if (nextJob?.status === "completed" || nextJob?.status === "failed") {
          window.clearInterval(intervalId);
          setPolling(false);
          applyCompletedJobState(nextJob);
        }
      } catch (error) {
        window.clearInterval(intervalId);
        setPolling(false);
        showToast(error?.message || "Failed to check scan status", "error");
      }
    }, 2500);

    return () => {
      window.clearInterval(intervalId);
      setPolling(false);
    };
  }, [job?.id, job?.status, showToast, applyCompletedJobState]);

  useEffect(() => {
    const processingItems = scanSession.filter(
      (item) => item.jobId && (item.jobStatus === "pending" || item.jobStatus === "processing")
    );
    if (processingItems.length === 0) return undefined;

    const intervalId = window.setInterval(async () => {
      await Promise.all(
        processingItems.map(async (item) => {
          try {
            const nextJob = await getCardScanJob({ id: item.jobId });
            setScanSession((prev) =>
              prev.map((entry) =>
                entry.id === item.id
                  ? {
                      ...entry,
                      job: nextJob,
                      jobStatus: nextJob?.status || entry.jobStatus,
                      captureStatus:
                        nextJob?.status === "completed"
                          ? "ready_for_review"
                          : nextJob?.status === "failed"
                          ? "failed"
                          : "scanning",
                      error: nextJob?.status === "failed" ? nextJob?.errorMessage || "Scan failed" : "",
                    }
                  : entry
              )
            );

            if (activeSessionId === item.id) {
              setJob(nextJob);
              if (nextJob?.status === "completed") {
                applyCompletedJobState(nextJob);
              }
            }
          } catch (error) {
            setScanSession((prev) =>
              prev.map((entry) =>
                entry.id === item.id
                  ? {
                      ...entry,
                      jobStatus: "failed",
                      captureStatus: "failed",
                      error: error?.message || "Failed to check scan status",
                    }
                  : entry
              )
            );
          }
        })
      );
    }, 2500);

    return () => window.clearInterval(intervalId);
  }, [scanSession, activeSessionId, applyCompletedJobState]);

  useEffect(() => {
    if (!cameraOpen) return undefined;
    if (!cameraSupported) {
      setCameraError("Camera access is not available in this browser. Use image upload instead.");
      return undefined;
    }

    let cancelled = false;
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await video.play().catch(() => undefined);
        }
        const track = stream.getVideoTracks?.()[0];
        const capabilities = track?.getCapabilities?.() || {};
        setCameraTorchSupported(Boolean(capabilities.torch));
        setCameraError("");
      } catch (error) {
        setCameraError(error?.message || "Unable to open the camera. You can still upload images.");
      }
    };

    void startCamera();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      setCameraTorchEnabled(false);
      setCameraTorchSupported(false);
    };
  }, [cameraOpen, cameraSupported]);

  const openCamera = useCallback((replaceTargetId = "") => {
    setCameraReplaceTargetId(replaceTargetId);
    setCameraStep("front");
    revokePreviewUrl(stagedFrontCapture?.preview);
    setStagedFrontCapture(null);
    setCameraError("");
    setCameraOpen(true);
  }, [stagedFrontCapture]);

  const closeCamera = (preserveStaged = false) => {
    const shouldCloseLaunchModal = !preserveStaged && initialCameraLaunchPendingRef.current;

    setCameraOpen(false);
    setCameraError("");
    setCameraTorchEnabled(false);
    setCameraTorchSupported(false);
    setCameraReplaceTargetId("");
    if (!preserveStaged) {
      revokePreviewUrl(stagedFrontCapture?.preview);
      setStagedFrontCapture(null);
    }

    if (shouldCloseLaunchModal) {
      initialCameraLaunchPendingRef.current = false;
      onRequestClose?.();
    }
  };

  useEffect(() => {
    if (launchHandledRef.current) return;
    launchHandledRef.current = true;
    if (launchMode === "camera" && cameraSupported) {
      openCamera("");
    }
  }, [cameraSupported, launchMode, openCamera]);

  useEffect(() => {
    onCameraVisibilityChange?.(cameraOpen);
  }, [cameraOpen, onCameraVisibilityChange]);

  const loadSessionItem = useCallback((item) => {
    if (!item) return;
    setActiveSessionId(item.id);
    setFrontFile(item.frontFile || null);
    setBackFile(item.backFile || null);
    setFrontPreview(item.frontPreview || "");
    setBackPreview(item.backPreview || "");
    setJob(item.job || null);
    setSelectedCandidate(item.job?.result?.selectedCandidate || null);
    setManualCard(null);
    setManualCardName("");
    setCondition(item.condition || "");
    setGradingCompany(item.gradingCompany || "raw");
    setGradeNumber(item.gradeNumber || "");
    setQuantity(item.quantity || "1");
    setShowEditDetails(false);
    setShowResultDetails(false);
    setManualOverrideMode(false);
    if (item.confirmedCard?.game) {
      setManualGame(item.confirmedCard.game);
    }
    if (item.job?.status === "completed") {
      applyCompletedJobState(item.job);
    }
  }, [applyCompletedJobState]);

  const applyCapturedPairToActiveScan = useCallback((pair) => {
    setActiveSessionId("");
    setFrontFile(pair.frontFile || null);
    setBackFile(pair.backFile || null);
    setFrontPreview(pair.frontPreview || "");
    setBackPreview(pair.backPreview || "");
    setJob(null);
    setSelectedCandidate(null);
    setManualCard(null);
    setManualCardName("");
    setShowEditDetails(false);
    setShowResultDetails(false);
    setManualOverrideMode(false);
  }, []);

  const upsertSessionItem = useCallback((nextItem, options = {}) => {
    const { makeActive = true } = options;
    setScanSession((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === nextItem.id);
      if (existingIndex === -1) return [nextItem, ...prev];
      return prev.map((item) => (item.id === nextItem.id ? nextItem : item));
    });
    if (makeActive) {
      loadSessionItem(nextItem);
    }
  }, [loadSessionItem]);

  const removeSessionItem = (itemId) => {
    setScanSession((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
              ...item,
              removedAt: new Date().toISOString(),
              removedReason: item.removedReason || "removed",
            }
          : item
      )
    );
    if (activeSessionId === itemId) {
      clearActiveWorkspace();
    }
  };

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track || !cameraTorchSupported) return;
    const nextValue = !cameraTorchEnabled;
    try {
      await track.applyConstraints({
        advanced: [{ torch: nextValue }],
      });
      setCameraTorchEnabled(nextValue);
    } catch (error) {
      setCameraError(error?.message || "Torch control is not supported on this device.");
    }
  };

  const captureFrame = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    if (!width || !height) {
      setCameraError("Camera is still starting. Try again in a moment.");
      return;
    }

    const stageRect = cameraStageRef.current?.getBoundingClientRect?.();
    const frameRect = cameraFrameRef.current?.getBoundingClientRect?.();
    const cropRegion = getGuideCropRegion({
      stageRect,
      frameRect,
      sourceWidth: width,
      sourceHeight: height,
    });

    const outputWidth = cropRegion?.sw || width;
    const outputHeight = cropRegion?.sh || height;

    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const context = canvas.getContext("2d");
    if (!context) {
      setCameraError("Could not capture image. Please try again.");
      return;
    }
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";

    if (cropRegion) {
      context.drawImage(
        video,
        cropRegion.sx,
        cropRegion.sy,
        cropRegion.sw,
        cropRegion.sh,
        0,
        0,
        outputWidth,
        outputHeight
      );
    } else {
      context.drawImage(video, 0, 0, width, height, 0, 0, outputWidth, outputHeight);
    }

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
    if (!blob) {
      setCameraError("Could not capture image. Please try again.");
      return;
    }

    const file = new File([blob], `scan-${cameraStep}-${Date.now()}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
    const preview = filePreviewUrl(file);

    if (cameraStep === "front") {
      revokePreviewUrl(stagedFrontCapture?.preview);
      setStagedFrontCapture({ file, preview });
      setCameraStep("back");
      return;
    }

    if (!stagedFrontCapture?.file) {
      setCameraError("Capture the front image first.");
      revokePreviewUrl(preview);
      return;
    }

    const existingOrder = scanSessionRef.current.find((item) => item.id === cameraReplaceTargetId)?.sessionOrder;
    const nextItem = makeSessionItem({
      id: cameraReplaceTargetId || createSessionItemId(),
      sessionItemId: cameraReplaceTargetId || undefined,
      sessionOrder: existingOrder,
      frontFile: stagedFrontCapture.file,
      backFile: file,
      frontPreview: stagedFrontCapture.preview,
      backPreview: preview,
      captureStatus: "captured",
    });

    if (batchModeEnabled) {
      initialCameraLaunchPendingRef.current = false;
      if (cameraReplaceTargetId) {
        setScanSession((prev) =>
          prev.map((item) =>
            item.id === cameraReplaceTargetId
              ? {
                  ...item,
                  ...nextItem,
                }
              : item
          )
        );
        loadSessionItem(nextItem);
      } else {
        setScanSession((prev) => [nextItem, ...prev]);
        loadSessionItem(nextItem);
      }

      showToast("Card added to scan queue.", "success");
      setCameraReplaceTargetId("");
      setStagedFrontCapture(null);
      setCameraStep("front");
      return;
    }

    if (isCollectionFlow) {
      initialCameraLaunchPendingRef.current = false;
      upsertSessionItem(nextItem);
      showToast("Card photos captured. You can scan this card now.", "success");
      setCameraReplaceTargetId("");
      setStagedFrontCapture(null);
      closeCamera(true);
      return;
    }

    initialCameraLaunchPendingRef.current = false;
    applyCapturedPairToActiveScan(nextItem);
    showToast("Card photos captured. You can scan this card now.", "success");
    setCameraReplaceTargetId("");
    setStagedFrontCapture(null);
    closeCamera(true);
  };

  const warnings = useMemo(() => {
    const list = Array.isArray(job?.result?.warnings) ? job.result.warnings : [];
    if (job?.status === "failed" && job?.errorMessage) {
      return [job.errorMessage, ...list];
    }
    return list;
  }, [job]);
  const summaryWarnings = useMemo(() => sanitizeScannerWarnings(warnings).slice(0, 3), [warnings]);
  const hasStandalonePair = Boolean(frontFile && (requiresBackImage ? backFile : true) && !activeSessionId);

  const handleFilePick = (event, side) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const validation = validateImageFile(file, { maxBytes: MAX_IMAGE_BYTES_DEFAULT });
    if (!validation.ok) {
      showToast(validation.error || "Please upload a valid image.", "error");
      event.target.value = "";
      return;
    }
    const nextPreview = filePreviewUrl(file);
    const replacingLoadedSessionItem = Boolean(activeSessionId);
    setActiveSessionId("");
    if (side === "front") {
      if (!replacingLoadedSessionItem) {
        revokePreviewUrl(frontPreview);
      }
      setFrontFile(file);
      setFrontPreview(nextPreview);
    } else {
      if (!replacingLoadedSessionItem) {
        revokePreviewUrl(backPreview);
      }
      setBackFile(file);
      setBackPreview(nextPreview);
    }
  };

  const resetScan = () => {
    revokePreviewUrl(frontPreview);
    revokePreviewUrl(backPreview);
    scanSession.forEach((item) => {
      revokePreviewUrl(item.frontPreview);
      revokePreviewUrl(item.backPreview);
    });
    setFrontFile(null);
    setBackFile(null);
    setFrontPreview("");
    setBackPreview("");
    setJob(null);
    setSelectedCandidate(null);
    setManualCard(null);
    setManualCardName("");
    setShowEditDetails(false);
    setShowResultDetails(false);
    setManualOverrideMode(false);
    setCondition("");
    setGradingCompany("raw");
    setGradeNumber("");
    setQuantity("1");
    setActiveSessionId("");
    setScanSession([]);
    setBatchModeEnabled(false);
    closeCamera();
  };

  const handleStartScan = async (targetItem = null) => {
    let sourceItem = targetItem || activeSessionItem;
    const sourceFrontFile = sourceItem?.frontFile || frontFile;
    const sourceBackFile = sourceItem?.backFile || backFile || (isCollectionFlow ? sourceFrontFile : null);

    if (!sourceFrontFile || !sourceBackFile) {
      showToast(
        isCollectionFlow ? "Please upload a front image." : "Please upload both front and back images.",
        "warning"
      );
      return;
    }

    if (isCollectionFlow && !sourceItem) {
      sourceItem = makeSessionItem({
        frontFile: sourceFrontFile,
        backFile: sourceBackFile,
        frontPreview,
        backPreview,
        captureStatus: "captured",
      });
      upsertSessionItem(sourceItem);
    }

    setSubmitting(true);
    setManualCard(null);
    setManualCardName("");
    setSelectedCandidate(null);
    setShowResultDetails(false);
    setManualOverrideMode(false);
    try {
      const payload = new FormData();
      payload.append("flowContext", flowContext);
      if (gameHint) {
        payload.append("gameHint", gameHint);
      }
      payload.append("frontFile", sourceFrontFile);
      payload.append("backFile", sourceBackFile);
      const createdJob = await createCardScanJob(payload);
      if (sourceItem?.id) {
        const updatedItem = {
          ...sourceItem,
          removedAt: "",
          removedReason: "",
          jobId: createdJob.id,
          jobStatus: createdJob.status,
          captureStatus: "scanning",
          error: "",
          job: createdJob,
        };
        upsertSessionItem(updatedItem);
      }
      setJob(createdJob);
      setShowResultDetails(false);
      setManualOverrideMode(false);
      showToast("Scanning started. We’ll fill in the card when analysis finishes.", "success");
    } catch (error) {
      showToast(error?.message || "Failed to start scan", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartBatchScan = async () => {
    if (batchScannableItems.length === 0) {
      showToast("There are no queued cards ready to scan.", "warning");
      return;
    }

    setSubmitting(true);
    setManualCard(null);
    setManualCardName("");
    setSelectedCandidate(null);
    setShowResultDetails(false);
    setManualOverrideMode(false);

    const primaryItemId =
      batchScannableItems.find((item) => item.id === activeSessionId)?.id ||
      batchScannableItems[0]?.id;

    let startedCount = 0;

    try {
      for (const item of batchScannableItems) {
        const payload = new FormData();
        payload.append("flowContext", flowContext);
        if (gameHint) {
          payload.append("gameHint", gameHint);
        }
        payload.append("frontFile", item.frontFile);
        payload.append("backFile", item.backFile);

        const createdJob = await createCardScanJob(payload);
        const updatedItem = {
          ...item,
          jobId: createdJob.id,
          jobStatus: createdJob.status,
          captureStatus: "scanning",
          error: "",
          job: createdJob,
        };

        upsertSessionItem(updatedItem, { makeActive: item.id === primaryItemId });
        if (item.id === primaryItemId) {
          setJob(createdJob);
        }
        startedCount += 1;
      }

      showToast(
        startedCount === 1
          ? "1 queued card started scanning."
          : `${startedCount} queued cards started scanning.`,
        "success"
      );
    } catch (error) {
      showToast(error?.message || "Failed to start batch scan", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (!job?.id) {
      showToast("Scan a card first.", "warning");
      return;
    }
    if (scannerBlocked && !manualOverrideMode) {
      showToast("Scanner confirmation is blocked for this scan. Select the correct card to continue.", "warning");
      return;
    }
    if (!selectedCard?.cardId || !selectedCard?.game) {
      showToast("Choose a card match before confirming.", "warning");
      return;
    }
    const normalizedQuantity = Math.max(1, Number.parseInt(String(quantity || "1"), 10) || 1);

    setConfirming(true);
    try {
      const response = await confirmCardScanJob({
        id: job.id,
        payload: {
          confirmationMode: scannerBlocked ? "manual_override" : "scanner",
          chosenCardGame: selectedCard.game,
          chosenCardId: selectedCard.cardId,
          chosenCardName: selectedCard.cardName,
          condition: isCollectionFlow ? condition || undefined : undefined,
          gradingCompany: isCollectionFlow ? gradingCompany || undefined : undefined,
          gradeNumber: isCollectionFlow && gradingCompany !== "raw" ? gradeNumber || undefined : undefined,
          quantity: isCollectionFlow ? String(normalizedQuantity) : undefined,
        },
      });
      showToast(
        isCollectionFlow
          ? isNavbarModalVariant
            ? "Card confirmed. Choose what to do next."
            : "Card added to Ready to Save."
          : "Card confirmed. Form updated.",
        "success",
      );
      setShowEditDetails(false);
      setShowResultDetails(false);
      setManualOverrideMode(false);
      setJob((prev) =>
        prev
          ? {
              ...prev,
              result: response?.scanResult || prev.result,
            }
          : prev
      );
      await Promise.resolve(onConfirmed?.(response?.confirmedCard, response));
      if (activeSessionId) {
        setScanSession((prev) =>
          prev.map((item) =>
            item.id === activeSessionId
              ? {
                  ...item,
                  captureStatus: "confirmed",
                  jobStatus: job?.status || item.jobStatus,
                  job: response?.scanResult ? { ...(job || item.job || {}), result: response.scanResult } : job,
                  collectionItemId: response?.collectionItem?.id || item.collectionItemId || "",
                  confirmedCard: response?.confirmedCard || selectedCard || item.confirmedCard || null,
                  condition: condition || item.condition || "",
                  gradingCompany: gradingCompany || item.gradingCompany || "raw",
                  gradeNumber: gradeNumber || item.gradeNumber || "",
                  quantity: String(normalizedQuantity),
                  saveOutcome: "",
                }
              : item
          )
        );
      }
      if (isCollectionFlow) {
        if (!isNavbarModalVariant) {
          clearActiveWorkspace();
        }
        await Promise.resolve(onCollectionChanged?.());
      }
    } catch (error) {
      showToast(error?.message || "Failed to confirm scanned card", "error");
    } finally {
      setConfirming(false);
    }
  };

  const handlePostTradeAction = async () => {
    if (!onPostTrade) return;
    if (!activeConfirmedSessionItem?.confirmedCard) {
      showToast("Load a confirmed card before posting a trade.", "warning");
      return;
    }

    try {
      await Promise.resolve(
        onPostTrade({
          sessionItem: activeConfirmedSessionItem,
          confirmedCard: activeConfirmedSessionItem.confirmedCard,
          collectionItemId: activeConfirmedSessionItem.collectionItemId || "",
          frontFile: activeConfirmedSessionItem.frontFile || frontFile || null,
          backFile: activeConfirmedSessionItem.backFile || backFile || null,
          frontPreview: activeConfirmedSessionItem.frontPreview || frontPreview || "",
          backPreview: activeConfirmedSessionItem.backPreview || backPreview || "",
          condition: activeConfirmedSessionItem.condition || condition || "",
          gradingCompany: activeConfirmedSessionItem.gradingCompany || gradingCompany || "raw",
          gradeNumber: activeConfirmedSessionItem.gradeNumber || gradeNumber || "",
          quantity: activeConfirmedSessionItem.quantity || quantity || "1",
          job: activeConfirmedSessionItem.job || job || null,
        }),
      );
    } catch (error) {
      showToast(error?.message || "Failed to prepare the trade draft", "error");
    }
  };

  const handleBulkSave = async () => {
    if (!isCollectionFlow || sessionSummary.eligibleSaveIds.length === 0) {
      showToast("There are no eligible session cards ready to save.", "warning");
      return;
    }

    setBulkSaving(true);
    try {
      const response = await saveCollectionItemsBulk({ ids: sessionSummary.eligibleSaveIds });
      const outcomeById = new Map((response?.results || []).map((item) => [item.id, item]));
      setScanSession((prev) =>
        prev.map((item) => {
          if (!item.collectionItemId) return item;
          const outcome = outcomeById.get(item.collectionItemId);
          if (!outcome || outcome.outcome === "failed" || outcome.outcome === "skipped_duplicate") return item;
          return {
            ...item,
            captureStatus: "saved",
            saveOutcome: outcome.outcome,
          };
        })
      );
      await Promise.resolve(onCollectionChanged?.());
      showToast(
        response?.mergedDuplicateCount
          ? `${response.savedCount} saved and ${response.mergedDuplicateCount} merged into existing collection cards.`
          : `${response?.savedCount || 0} cards saved to your collection.`,
        "success"
      );
    } catch (error) {
      showToast(error?.message || "Failed to save session cards", "error");
    } finally {
      setBulkSaving(false);
    }
  };

  const handleRemoveDuplicates = async () => {
    if (!isCollectionFlow || sessionSummary.removableDuplicateSessionIds.length === 0) {
      showToast("There are no duplicate session cards to remove.", "warning");
      return;
    }

    setBulkDiscarding(true);
    try {
      if (sessionSummary.duplicateDiscardIds.length > 0) {
        await discardCollectionItemsBulk({ ids: sessionSummary.duplicateDiscardIds });
      }
      setScanSession((prev) =>
        prev.map((item) =>
          sessionSummary.removableDuplicateSessionIds.includes(item.id)
            ? {
                ...item,
                removedAt: new Date().toISOString(),
                removedReason: "duplicate",
                collectionItemId:
                  sessionSummary.duplicateDiscardIds.includes(item.collectionItemId) ? "" : item.collectionItemId,
              }
            : item
        )
      );
      if (activeSessionId && sessionSummary.removableDuplicateSessionIds.includes(activeSessionId)) {
        clearActiveWorkspace();
      }
      await Promise.resolve(onCollectionChanged?.());
      showToast("Duplicate session cards removed.", "success");
    } catch (error) {
      showToast(error?.message || "Failed to remove duplicates", "error");
    } finally {
      setBulkDiscarding(false);
    }
  };

  const handleExportSession = () => {
    if (scanSession.length === 0) {
      showToast("There is no scan session to export yet.", "warning");
      return;
    }

    downloadSessionPdf({
      filename: `collection-scan-session-${new Date().toISOString().slice(0, 10)}.pdf`,
      title: "Collection Scan Session",
      generatedAt: new Date().toLocaleString(),
      summary: [
        { label: "Total scanned", value: sessionSummary.totalScanned },
        { label: "Added", value: sessionSummary.added },
        { label: "Duplicates", value: sessionSummary.duplicates },
        { label: "Errors", value: sessionSummary.errors },
      ],
      rows: sessionSummary.exportRows,
    });
    showToast("Session PDF downloaded.", "success");
  };

  return (
    <div className="scan-panel">
      <div className="scan-panel-header">
        <div>
          <h3>{title}</h3>
          <p>{subtitle}</p>
        </div>
        <div className="scan-panel-header-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => openCamera("")}
            disabled={!cameraSupported}
          >
            Open Camera
          </button>
          <button type="button" className="btn-secondary" onClick={resetScan}>
            Reset
          </button>
        </div>
      </div>

      {!cameraSupported && (
        <div className="scan-warning-list">
          <div className="scan-warning-item">Camera preview is unavailable in this browser. You can still upload front and back images.</div>
        </div>
      )}

      <div className="scan-upload-grid">
        <label className="scan-upload-card">
          <span>Front Image *</span>
          <input type="file" accept={IMAGE_INPUT_ACCEPT_ATTR} onChange={(event) => handleFilePick(event, "front")} />
          {(frontPreview || externalFrontPreview) ? (
            <img src={frontPreview || externalFrontPreview} alt="Front preview" />
          ) : (
            <div className="scan-upload-placeholder">Upload front photo</div>
          )}
        </label>

        {requiresBackImage && (
          <label className="scan-upload-card">
            <span>Back Image *</span>
            <input type="file" accept={IMAGE_INPUT_ACCEPT_ATTR} onChange={(event) => handleFilePick(event, "back")} />
            {(backPreview || externalBackPreview) ? (
              <img src={backPreview || externalBackPreview} alt="Back preview" />
            ) : (
              <div className="scan-upload-placeholder">Upload back photo</div>
            )}
          </label>
        )}
      </div>

      {visibleSessionItems.length > 0 && (
        <div className="scan-session-panel">
          <div className="scan-session-header">
            <div>
              <h4>Batch Scan Session</h4>
              <p>Capture one or more cards, then scan each pair when you’re ready.</p>
            </div>
            <button type="button" className="btn-secondary" onClick={() => openCamera("")} disabled={!cameraSupported}>
              Add Another Card
            </button>
          </div>
          <div className="scan-session-list">
            {visibleSessionItems.map((item, index) => {
              const isActive = item.id === activeSessionId;
              const sessionStateLabel = getSessionStateLabel(item);
              const detail = sessionSummary.detailsById[item.id] || {};
              return (
                <article key={item.id} className={`scan-session-item ${isActive ? "active" : ""}`}>
                  <div className="scan-session-thumbs">
                    {item.frontPreview ? <img src={item.frontPreview} alt={`Card ${index + 1} front`} /> : <div className="scan-session-thumb-placeholder">Front</div>}
                    {(requiresBackImage || item.backPreview) ? (
                      item.backPreview ? <img src={item.backPreview} alt={`Card ${index + 1} back`} /> : <div className="scan-session-thumb-placeholder">Back</div>
                    ) : null}
                  </div>
                  <div className="scan-session-copy">
                    <div className="scan-session-copy-header">
                      <strong>{`Card ${index + 1}`}</strong>
                      <span className={`scan-session-status status-${item.captureStatus || "captured"}`}>{sessionStateLabel}</span>
                    </div>
                    <span className="scan-session-subcopy">{getSessionStateCopy(item)}</span>
                    {detail.duplicateClassification ? (
                      <span className="scan-session-flag">
                        Duplicate: {detail.duplicateClassification === "saved_collection" ? "matches saved collection" : "matches another session card"}
                      </span>
                    ) : null}
                    {item.error ? <span className="scan-session-error">{item.error}</span> : null}
                  </div>
                  <div className="scan-session-actions">
                    <button type="button" className="btn-secondary" onClick={() => loadSessionItem(item)}>
                      {item.captureStatus === "ready_for_review" || item.captureStatus === "confirmed" ? "Review" : "Use Pair"}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => void handleStartScan(item)}
                      disabled={submitting || item.captureStatus === "scanning" || item.captureStatus === "saved"}
                    >
                      {item.captureStatus === "scanning"
                        ? "Scanning..."
                        : item.captureStatus === "failed"
                        ? "Retry"
                        : item.captureStatus === "saved"
                        ? "Saved"
                        : "Scan"}
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => openCamera(item.id)} disabled={!cameraSupported}>
                      Retake
                    </button>
                    <button type="button" className="btn-secondary scan-remove-btn" onClick={() => removeSessionItem(item.id)}>
                      Remove
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      )}

      {isCollectionFlow && scanSession.length > 0 && (
        <div className="scan-session-summary">
          <div className="scan-session-summary-header">
            <div>
              <h4>Session Summary</h4>
              <p>Track what was scanned in this page session and manage the staged collection results in bulk.</p>
            </div>
          </div>
          <div className="scan-session-summary-grid">
            <div className="scan-session-summary-stat">
              <span>Total scanned</span>
              <strong>{sessionSummary.totalScanned}</strong>
            </div>
            <div className="scan-session-summary-stat">
              <span>Added</span>
              <strong>{sessionSummary.added}</strong>
            </div>
            <div className="scan-session-summary-stat">
              <span>Duplicates</span>
              <strong>{sessionSummary.duplicates}</strong>
            </div>
            <div className="scan-session-summary-stat">
              <span>Errors</span>
              <strong>{sessionSummary.errors}</strong>
            </div>
          </div>
          <div className="scan-session-summary-actions">
            {!isNavbarModalVariant && (
              <button type="button" className="btn-secondary" onClick={handleBulkSave} disabled={bulkSaving || sessionSummary.eligibleSaveIds.length === 0}>
                {bulkSaving ? "Saving..." : "Add All to Collection"}
              </button>
            )}
            <button
              type="button"
              className="btn-secondary"
              onClick={handleRemoveDuplicates}
              disabled={bulkDiscarding || sessionSummary.removableDuplicateSessionIds.length === 0}
            >
              {bulkDiscarding ? "Removing..." : "Remove Duplicates"}
            </button>
            <button type="button" className="btn-secondary" onClick={handleExportSession}>
              Export Session List
            </button>
          </div>
        </div>
      )}

      {isNavbarModalVariant && activeConfirmedSessionItem?.confirmedCard && (
        <div className="scan-follow-up-actions">
          <div className="scan-follow-up-copy">
            <h4>Next Step</h4>
            <p>Keep this card in Ready to Save, add all eligible session cards to your collection, or use the active confirmed card for a trade draft.</p>
          </div>
          <div className="scan-follow-up-buttons">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleBulkSave}
              disabled={bulkSaving || sessionSummary.eligibleSaveIds.length === 0}
            >
              {bulkSaving ? "Saving..." : "Add to Collection"}
            </button>
            <button type="button" className="btn-primary" onClick={handlePostTradeAction}>
              Post a Trade
            </button>
          </div>
        </div>
      )}

      <div className="scan-actions">
        <button
          type="button"
          className="btn-primary"
          disabled={submitting || polling || (!hasStandalonePair && visibleSessionItems.length > 0 && batchScannableItems.length === 0)}
          onClick={hasStandalonePair ? handleStartScan : visibleSessionItems.length > 0 ? handleStartBatchScan : handleStartScan}
        >
          {submitting
            ? !hasStandalonePair && visibleSessionItems.length > 0
              ? "Starting Batch Scan..."
              : "Starting Scan..."
            : polling
            ? "Scanning..."
            : !hasStandalonePair && visibleSessionItems.length > 0
            ? batchScannableItems.length === 1
              ? "Scan Card"
              : "Scan All Cards"
            : "Scan Card"}
        </button>
        {job?.status && (
          <span className={`scan-status status-${job.status}`}>
            {job.status === "processing" || job.status === "pending"
              ? `${progressStage} • ${progressPercent}%`
              : job.status === "completed"
              ? "Scan complete"
              : "Scan failed"}
          </span>
        )}
      </div>

      {scanInProgress && (
        <div className="scan-loading-card" aria-live="polite">
          <div className="scan-loading-header">
            <div>
              <h4>{progressStage}</h4>
              <p>We’re processing the front and back images, then matching the result against your card catalog.</p>
            </div>
            <span className="scan-loading-percent">{progressPercent}%</span>
          </div>
          <div className="scan-loading-track" aria-hidden="true">
            <span className="scan-loading-fill" style={{ width: `${Math.min(100, Math.max(6, progressPercent))}%` }} />
          </div>
          <div className="scan-loading-grid" aria-hidden="true">
            <div className="scan-loading-skeleton" />
            <div className="scan-loading-skeleton" />
            <div className="scan-loading-skeleton" />
            <div className="scan-loading-skeleton" />
          </div>
        </div>
      )}

      {!scanCompleted && warnings.length > 0 && (
        <div className="scan-warning-list">
          {warnings.map((warning) => (
            <div key={warning} className="scan-warning-item">
              {warning}
            </div>
          ))}
        </div>
      )}

      {scanSupportWarning && (
        <div className="scan-warning-list">
          <div className="scan-warning-item">{scanSupportWarning}</div>
        </div>
      )}

      {scanCompleted && job?.result?.analysis && (
        <div className="scan-analysis-card">
          {primaryResultCard && (
            <div className="scan-result-overview">
              {job?.frontImageUrl ? (
                <img className="scan-result-image" src={job.frontImageUrl} alt={primaryResultCard.cardName || "Scanned card"} />
              ) : (
                <div className="scan-result-image scan-result-placeholder">No image</div>
              )}
              <div className="scan-result-copy">
                <h4>{primaryResultCard.cardName || "Scanned card"}</h4>
                <p>{primaryResultCard.setName || "Set not identified"}</p>
                {primaryResultCard.cardNumber && (
                  <span className="scan-result-meta">Card Number: {primaryResultCard.cardNumber}</span>
                )}
                {showSummaryConfidence && (
                  <span className="scan-result-meta">
                    Scan Confidence: {recognitionConfidence}%
                  </span>
                )}
                <div className="scan-market-card">
                  <span className="scan-market-label">Latest market price</span>
                  {primaryMarketHeadline ? (
                    <strong className="scan-market-price">{primaryMarketHeadline}</strong>
                  ) : (
                    <strong className="scan-market-price unavailable">Pricing unavailable</strong>
                  )}
                  {primaryMarketRange && (
                    <span className="scan-market-meta">Low/High: {primaryMarketRange}</span>
                  )}
                  {primaryMarketSource && (
                    <span className="scan-market-meta">{primaryMarketSource}</span>
                  )}
                  {primaryMarketFetchedAt && (
                    <span className="scan-market-meta">Updated {primaryMarketFetchedAt}</span>
                  )}
                </div>
                <span className={`scan-auth-chip decision-${authenticityDecision}`}>
                  {authenticityDecisionLabel}
                </span>
              </div>
            </div>
          )}
          {job.result.analysis?.explanation && (
            <p className="scan-analysis-note">{job.result.analysis.explanation}</p>
          )}
          {summaryWarnings.length > 0 && (
            <div className="scan-summary-issues">
              {summaryWarnings.map((warning) => (
                <div key={warning} className="scan-summary-issue">
                  {warning}
                </div>
              ))}
            </div>
          )}
          <div className="scan-summary-next-step">
            {getRecommendedActionLabel(recommendedAction)}
          </div>
          <div className="scan-result-actions">
            <button type="button" className="btn-secondary" onClick={() => setShowResultDetails((value) => !value)}>
              {showResultDetails ? "Hide Scan Details" : "Show Scan Details"}
            </button>
            {(scannerBlocked || !selectedCard) && !manualOverrideMode && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setManualOverrideMode(scannerBlocked);
                  setShowEditDetails(true);
                }}
              >
                Select Correct Card
              </button>
            )}
            <button type="button" className="btn-secondary scan-reject-btn" onClick={resetScan}>
              Reject & Retry
            </button>
          </div>
          {showResultDetails && (
            <div className="scan-details-drawer">
              <div className="scan-analysis-grid">
                <div>
                  <strong>Scanner Decision</strong>
                  <div>{authenticityDecisionLabel}</div>
                </div>
                {detectedGame && (
                  <div>
                    <strong>Detected Game</strong>
                    <div>{detectedGame}</div>
                  </div>
                )}
                {showSummaryConfidence && (
                  <div>
                    <strong>Scan Confidence</strong>
                    <div>{recognitionConfidence}%</div>
                  </div>
                )}
                <div>
                  <strong>Condition Estimate</strong>
                  <div>{job.result.analysis?.conditionEstimate?.label || "Unknown"}</div>
                </div>
                <div>
                  <strong>Likely Grade Range</strong>
                  <div>
                    {job.result.analysis?.likelyGradeRange?.min != null || job.result.analysis?.likelyGradeRange?.max != null
                      ? `${job.result.analysis.likelyGradeRange?.min ?? "?"} - ${job.result.analysis.likelyGradeRange?.max ?? "?"}`
                      : "Unknown"}
                  </div>
                </div>
                <div>
                  <strong>Authenticity Risk</strong>
                  <div>{job.result.analysis?.authenticityRisk?.level || "Unknown"}</div>
                </div>
              </div>
              {authenticityChecks.length > 0 && (
                <div className="scan-check-list">
                  {authenticityChecks.map((check) => (
                    <div key={`${check.name}-${check.label}`} className={`scan-check-item status-${check.status || "unverified"}`}>
                      <div className="scan-check-header">
                        <strong>{check.label}</strong>
                        <span>{check.status || "unverified"}</span>
                      </div>
                      <p>{check.summary || "No summary provided."}</p>
                    </div>
                  ))}
                </div>
              )}
              {missingEvidence.length > 0 && (
                <div className="scan-missing-evidence">
                  <strong>Missing Evidence</strong>
                  <ul>
                    {missingEvidence.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {scanCompleted && showResultDetails && candidates.length > 0 && (
        <div className="scan-candidate-list">
          <h4>Matched Candidates</h4>
          {candidates.map((candidate) => {
            const isActive = selectedCandidate?.cardId === candidate.cardId && selectedCandidate?.game === candidate.game;
            return (
              <button
                key={`${candidate.game}:${candidate.cardId}`}
                type="button"
                className={`scan-candidate-item ${isActive ? "active" : ""}`}
                onClick={() => {
                  setSelectedCandidate(candidate);
                  setManualCard(null);
                  setManualCardName("");
                  setManualGame(candidate.game || gameHint || DEFAULT_GAME_OPTIONS[0]);
                }}
              >
                <span className="scan-candidate-main">
                  {candidate.cardName}
                  <span className="scan-candidate-confidence">Catalog match {candidate.confidence || 0}%</span>
                </span>
                <span className="scan-candidate-meta">
                  {[candidate.game, candidate.setName, candidate.cardNumber].filter(Boolean).join(" • ")}
                </span>
                <span className="scan-candidate-meta">
                  {candidate.market?.market != null || candidate.market?.mid != null || candidate.market?.low != null || candidate.market?.high != null
                    ? `Price ${formatMarketPrice(
                        candidate.market?.market ?? candidate.market?.mid ?? candidate.market?.low ?? candidate.market?.high,
                        candidate.market?.currency
                      )}`
                    : "Pricing unavailable"}
                </span>
                {candidate.comparisonSummary && (
                  <span className="scan-candidate-meta">{candidate.comparisonSummary}</span>
                )}
                {candidate.mismatchCount > 0 && (
                  <span className="scan-candidate-meta">{candidate.mismatchCount} comparison mismatch{candidate.mismatchCount === 1 ? "" : "es"}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {scanCompleted && showEditDetails && (
        <div className="scan-manual-review">
          <h4>{manualOverrideMode ? "Review Card Choice" : "Confirm or Edit"}</h4>
          {manualOverrideMode && manualOverrideNote && (
            <div className="scan-manual-note">{manualOverrideNote}</div>
          )}
          <div className="scan-manual-grid">
            <div className="form-group">
              <label>Game</label>
              <SharedSelect value={manualGame} onChange={(event) => {
                setManualGame(event.target.value);
                setManualCard(null);
              }}>
                {DEFAULT_GAME_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </SharedSelect>
            </div>
            <div className="form-group full-width">
              <label>Final Card</label>
              <CardAutocompleteInput
                game={activeGame}
                value={manualCardName}
                onValueChange={setManualCardName}
                selectedCard={manualCard}
                onSelectCard={(card) => {
                  setManualCard(card);
                  if (card) {
                    setSelectedCandidate(null);
                  }
                }}
                placeholder="Search to correct the scan result"
              />
            </div>
          </div>

          <div className="scan-market-card scan-market-card-manual">
            <span className="scan-market-label">Selected card pricing</span>
            {selectedCardMarketLoading ? (
              <strong className="scan-market-price">Loading pricing...</strong>
            ) : selectedMarketHeadline ? (
              <strong className="scan-market-price">{selectedMarketHeadline}</strong>
            ) : (
              <strong className="scan-market-price unavailable">Pricing unavailable</strong>
            )}
            {selectedMarketRange && (
              <span className="scan-market-meta">Low/High: {selectedMarketRange}</span>
            )}
            {selectedMarketSource && (
              <span className="scan-market-meta">{selectedMarketSource}</span>
            )}
            {selectedMarketFetchedAt && (
              <span className="scan-market-meta">Updated {selectedMarketFetchedAt}</span>
            )}
          </div>

          {isCollectionFlow && (
            <div className="scan-collection-fields">
              <div className="form-group">
                <label>Quantity</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  value={quantity}
                  onChange={(event) => setQuantity(event.target.value)}
                  placeholder="1"
                />
              </div>
              <div className="form-group">
                <label>Condition</label>
                <SharedSelect value={condition} onChange={(event) => setCondition(event.target.value)}>
                  <option value="">Unknown</option>
                  <option value="Near Mint">Near Mint</option>
                  <option value="Lightly Played">Lightly Played</option>
                  <option value="Moderately Played">Moderately Played</option>
                  <option value="Heavily Played">Heavily Played</option>
                  <option value="Damaged">Damaged</option>
                </SharedSelect>
              </div>
              <div className="form-group">
                <label>Grading Company</label>
                <SharedSelect value={gradingCompany} onChange={(event) => setGradingCompany(event.target.value)}>
                  <option value="raw">Raw (Ungraded)</option>
                  <option value="psa">PSA</option>
                  <option value="bgs">BGS</option>
                  <option value="cgc">CGC</option>
                  <option value="tag">TAG</option>
                  <option value="sgc">SGC</option>
                  <option value="other">Other</option>
                </SharedSelect>
              </div>
              <div className="form-group">
                <label>Grade Number</label>
                <input
                  type="text"
                  value={gradeNumber}
                  onChange={(event) => setGradeNumber(event.target.value)}
                  placeholder="Optional"
                  disabled={gradingCompany === "raw"}
                />
              </div>
            </div>
          )}

          <button
            type="button"
            className="btn-primary"
            disabled={confirming || !selectedCard || (scannerBlocked && !manualOverrideMode)}
            onClick={handleConfirm}
          >
            {confirming
              ? "Confirming..."
              : manualOverrideMode
              ? isCollectionFlow
                ? isNavbarModalVariant
                  ? "Use Selected Card"
                  : "Use Selected Card and Save"
                : "Use Selected Card"
              : isCollectionFlow
              ? isNavbarModalVariant
                ? "Confirm Card"
                : "Confirm and Save"
              : "Use This Card"}
          </button>
        </div>
      )}

      {scanCompleted && !showEditDetails && (
        <button
          type="button"
          className="btn-primary"
          disabled={confirming || !selectedCard || scannerBlocked}
          onClick={handleConfirm}
        >
          {confirming ? "Confirming..." : isCollectionFlow ? (isNavbarModalVariant ? "Confirm Card" : "Confirm and Save") : "Use This Card"}
        </button>
      )}

      {cameraOpen && typeof document !== "undefined"
        ? createPortal(
            <div className="scan-camera-modal" role="dialog" aria-modal="true" aria-label="Scanner camera">
              <div className="scan-camera-backdrop" onClick={closeCamera} />
              <div className="scan-camera-shell">
                <div className="scan-camera-toolbar">
                  <div>
                    <h4>{cameraStep === "front" ? "Capture front" : "Capture back"}</h4>
                    <p>{cameraStep === "front" ? "Frame the front of the card inside the guide." : "Flip the card and capture the back."}</p>
                  </div>
                  <div className="scan-camera-toolbar-actions">
                    {cameraTorchSupported && (
                      <button type="button" className="btn-secondary" onClick={toggleTorch}>
                        {cameraTorchEnabled ? "Flash Off" : "Flash On"}
                      </button>
                    )}
                    <button type="button" className="btn-secondary" onClick={closeCamera}>
                      Close
                    </button>
                  </div>
                </div>
                <label className="scan-batch-toggle">
                  <input
                    type="checkbox"
                    checked={batchModeEnabled}
                    onChange={(event) => setBatchModeEnabled(event.target.checked)}
                  />
                  <span>Scan multiple cards in this session</span>
                </label>
                <div ref={cameraStageRef} className="scan-camera-stage">
                  <video ref={videoRef} playsInline muted autoPlay className="scan-camera-video" />
                  <div ref={cameraFrameRef} className="scan-camera-frame" aria-hidden="true" />
                  <div className="scan-camera-hint">{cameraStep === "front" ? "Capture front" : "Capture back"}</div>
                </div>
                {cameraError ? <div className="scan-warning-item">{cameraError}</div> : null}
                <div className="scan-camera-footer">
                  <button type="button" className="btn-primary" onClick={captureFrame}>
                    {cameraStep === "front" ? "Capture Front" : "Capture Back"}
                  </button>
                </div>
                <canvas ref={canvasRef} className="scan-camera-canvas" aria-hidden="true" />
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
